import React from 'react';
import { Box, TextField, IconButton, Tooltip } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * A from/to date pair that carries its own spacing.
 *
 * Exists because the first attempt dropped two loose <TextField>s into each
 * page's existing toolbar row — rows that were already sized for what was in
 * them (KYC Details' search row is capped at maxWidth 40% with nowrap), so
 * 310px of extra controls crushed everything beside them.
 *
 * Grouping them means the pair wraps as ONE unit to the next line instead of
 * splitting across a break, and every page gets identical gaps without each
 * one re-deciding. `flexShrink: 0` keeps the inputs at a usable width rather
 * than letting a tight row squeeze them to unreadable stubs.
 */

interface Props {
  from: string;
  to: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  /** Names the column, e.g. "Paid" gives "Paid from" / "Paid to". */
  label?: string;
  size?: 'small' | 'medium';
  /** Matches the muted field background some toolbars use. */
  background?: string;
}

const DateRangeFilter: React.FC<Props> = ({
  from,
  to,
  onFromChange,
  onToChange,
  label = 'Date',
  size = 'small',
  background,
}) => {
  const inverted = !!from && !!to && from > to;

  const field = {
    minWidth: 150,
    flexShrink: 0,
    ...(background ? { background } : {}),
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        flexShrink: 0,
        flexWrap: 'nowrap',
      }}
    >
      <TextField
        size={size}
        type="date"
        label={`${label} from`}
        value={from}
        onChange={(e) => onFromChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
        error={inverted}
        sx={field}
      />
      <TextField
        size={size}
        type="date"
        label={`${label} to`}
        value={to}
        onChange={(e) => onToChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
        error={inverted}
        // helperText only on the second field: one message for the pair, and it
        // sits under the input that is actually wrong.
        helperText={inverted ? 'From is after To' : undefined}
        sx={field}
      />
      {(from || to) && (
        <Tooltip title="Clear dates">
          <IconButton
            size="small"
            onClick={() => {
              onFromChange('');
              onToChange('');
            }}
            sx={{ mt: 0.5, flexShrink: 0 }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default DateRangeFilter;
